import { useRouter } from 'next/router';
import { useCallback, useMemo, type FunctionComponent } from 'react';

import { Pagination as PaginationTailwind } from '@/core/tamagoshiTailwind/components/Pagination/Pagination';
import { getPlatform } from '@/domains/core/settings/utils';
import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import { useNbProducts } from '@/productDiscovery/Listings/hooks/useNbProducts';
import { usePageName } from '@/productDiscovery/Listings/hooks/usePageName';

import { PaginationClickEvent } from './interface';
import {
  generatePaginationUrl,
  getProductsPerPage,
  getTotalPages,
} from './Pagination.utils';
import { paginationNextPage, paginationPreviousPage } from './translation';

export interface PaginationProps {
  id?: string;
  className?: string;
  /**
   *  Maximum number of native links <a> allowed in the page for SEO purposes.
   * If undefined, all links are native <a> (default value).
   * If set to 0, native links are not allowed. Links will be rendered as <span>.
   */
  nbNativeLinksAllowed?: number;
}

export const Pagination: FunctionComponent<PaginationProps> = ({
  id,
  className,
  nbNativeLinksAllowed = undefined,
}) => {
  const nbProducts = useNbProducts();
  const router = useRouter();
  const { asPath, query } = router;
  const pageName = usePageName();

  const currentPage = useMemo(() => Number(query.page) || 1, [query.page]);

  const shouldUseNativeLink = useCallback(
    (page: number) =>
      nbNativeLinksAllowed === undefined || page <= nbNativeLinksAllowed,
    [nbNativeLinksAllowed],
  );

  const generateUrl = useCallback(
    (page: number) => generatePaginationUrl(asPath, page),
    [asPath],
  );

  const productsPerPage = useMemo(
    () => getProductsPerPage(pageName),
    [pageName],
  );

  const onPageChange = useCallback(
    (page: number) => {
      if (!shouldUseNativeLink(page)) {
        router.push(generateUrl(page) as string, undefined, { shallow: true });
      }

      window.scrollTo(0, 0);

      Gtm.push<PaginationClickEvent>({
        event: 'interaction_click on pagination',
        e_category: 'navigation',
        e_action: 'Click on pagination',
        e_label: page,
        amp_event_name: 'Click on pagination',
        event_trigger: pageName,
        pagination_number: page,
        interaction_hit: 'False',
      });
    },
    [generateUrl, pageName, router, shouldUseNativeLink],
  );

  const totalPages = useMemo(
    () => getTotalPages(productsPerPage, nbProducts),
    [nbProducts, productsPerPage],
  );

  if (nbProducts === undefined) return null;

  return (
    <div id={id} className={className}>
      <PaginationTailwind
        currentPage={currentPage}
        count={totalPages}
        hrefForPage={(page) =>
          shouldUseNativeLink(page) ? generateUrl(page) : ''
        }
        previousPageAriaLabel={paginationPreviousPage}
        nextPageAriaLabel={paginationNextPage}
        onPageChange={onPageChange}
        levels={
          getPlatform() === 'it' && pageName === 'category' && !query.model_id
            ? [10, 20, 30]
            : undefined
        }
      />
    </div>
  );
};

Pagination.displayName = 'Pagination';
